import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, Users, ArrowRight } from "lucide-react";
import photographerImage from "@/assets/photographer-action.jpg";
import participantImage from "@/assets/participant-finding-photos.jpg";

const AudienceSection = () => {
  const { t } = useTranslation();

  const audiences = [
    {
      icon: Camera,
      title: t("audience.photographers.title"),
      description: t("audience.photographers.description"),
      features: [
        t("audience.photographers.feature1"),
        t("audience.photographers.feature2"),
        t("audience.photographers.feature3"),
        t("audience.photographers.feature4"),
      ],
      gradient: "from-hawk-orange to-hawk-gold",
      image: photographerImage,
      link: "/for-photographers",
    },
    {
      icon: Users,
      title: t("audience.participants.title"),
      description: t("audience.participants.description"),
      features: [
        t("audience.participants.feature1"),
        t("audience.participants.feature2"),
        t("audience.participants.feature3"),
        t("audience.participants.feature4"),
      ],
      gradient: "from-blue-500 to-cyan-400",
      image: participantImage,
      link: "/for-participants",
    },
  ];

  return (
    <section className="py-24 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
            {t("audience.heading")} <span className="text-hawk-orange">{t("audience.headingHighlight")}</span>
          </h2>
          <p className="text-lg text-muted-foreground">{t("audience.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {audiences.map((audience) => (
            <Link key={audience.title} to={audience.link} className="group">
              <Card variant="elevated" className="overflow-hidden h-full">
                <div className="relative h-48 overflow-hidden">
                  <img src={audience.image} alt={audience.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                </div>
                <CardContent className="p-8 pt-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${audience.gradient} flex items-center justify-center mb-4 -mt-10 relative z-10 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <audience.icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-display font-bold mb-3">{audience.title}</h3>
                  <p className="text-muted-foreground mb-6">{audience.description}</p>
                  <ul className="space-y-3 mb-6">
                    {audience.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${audience.gradient} flex items-center justify-center flex-shrink-0`}>
                          <ArrowRight className="text-white" size={12} />
                        </div>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-2 text-hawk-orange font-semibold text-sm group-hover:gap-3 transition-all">
                    {t("audience.learnMore")} <ArrowRight size={16} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AudienceSection;
